import { RangeSetBuilder, StateEffect, StateField } from "@codemirror/state";
import { Decoration, DecorationSet, EditorView } from "@codemirror/view";

export interface IKnownVariableNames {
  question: Set<string>;
  response: Set<string>;
}

const EMPTY_KNOWN_VARIABLES: IKnownVariableNames = {
  question: new Set(),
  response: new Set(),
};

const questionVariableMark = Decoration.mark({ class: "cm-questionVariable" });
const responseVariableMark = Decoration.mark({ class: "cm-responseVariable" });

// Same "anything but whitespace/parens/comma/quotes" token boundary the completion sources use
// (equationCompletions.ts) — a hyphenated variable like "hia-_billju_1_yes" is one token here
// too, not fragmented at the hyphen.
const WORD_PATTERN = /[^\s(),"']+/g;

const buildDecorations = (
  docText: string,
  known: IKnownVariableNames,
): DecorationSet => {
  const builder = new RangeSetBuilder<Decoration>();
  WORD_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = WORD_PATTERN.exec(docText))) {
    const word = match[0];
    const start = match.index;
    const end = start + word.length;
    // Question/response variable identity is data-dependent (fetched from the API), not a
    // static grammar rule — rqlLanguage.ts's StreamLanguage tokenizer has no way to know it, so
    // it's cross-checked here against the live known-variable sets instead. Question is checked
    // first since the two lists are expected to be disjoint in practice; order only matters if a
    // name were ever duplicated across both.
    if (known.question.has(word)) {
      builder.add(start, end, questionVariableMark);
    } else if (known.response.has(word)) {
      builder.add(start, end, responseVariableMark);
    }
  }
  return builder.finish();
};

// Finds the known variable (if any) whose token range contains `pos`, inclusive of both ends —
// inclusive on the end specifically so that right after an @/# autocomplete insertion, where the
// cursor sits exactly at the end of the just-inserted variable, it still counts as "on" it. Used
// by EquationCodeMirrorEditor to track cursor-position- and mouse-hover-based active variables
// (a persistent highlight, distinct from decoration coloring above, which those drive in
// QuestionListContainer).
export const findVariableTokenAtPos = (
  docText: string,
  known: IKnownVariableNames,
  pos: number,
): string | null => {
  WORD_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = WORD_PATTERN.exec(docText))) {
    const word = match[0];
    const start = match.index;
    const end = start + word.length;
    if (start > pos) {
      return null;
    }
    if (pos >= start && pos <= end) {
      return known.question.has(word) || known.response.has(word)
        ? word
        : null;
    }
  }
  return null;
};

// Dispatched (see EquationCodeMirrorEditor's knownVariables effect) whenever the live
// question/response variable sets change upstream (new `questions` prop), without needing to
// tear down and rebuild the EditorView.
export const setKnownVariablesEffect = StateEffect.define<IKnownVariableNames>();

export const knownVariablesField = StateField.define<IKnownVariableNames>({
  create: () => EMPTY_KNOWN_VARIABLES,
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setKnownVariablesEffect)) {
        return effect.value;
      }
    }
    return value;
  },
});

const decorationsField = StateField.define<DecorationSet>({
  create(state) {
    return buildDecorations(state.doc.toString(), state.field(knownVariablesField));
  },
  update(decorations, tr) {
    if (
      tr.docChanged ||
      tr.effects.some((effect) => effect.is(setKnownVariablesEffect))
    ) {
      return buildDecorations(
        tr.state.doc.toString(),
        tr.state.field(knownVariablesField),
      );
    }
    return decorations.map(tr.changes);
  },
  provide: (field) => EditorView.decorations.from(field),
});

// The full extension to add to EditorState.create's extensions array — bundles the known-
// variable data field and the decoration field it drives.
export const variableHighlightExtension = [knownVariablesField, decorationsField];
